import type { CreateUserData } from '@/shared/types/user';
import { rolesApi } from '@/features/roles';
import type { RoleOption } from '@/features/roles';
import { API_ENDPOINTS } from '@/constants/api';
import { useUserForm } from '@/features/users/hooks/useUserForm';
import { EntityForm } from '@/shared/components/EntityForm';
import { PageLayout } from '@/shared/components/PageLayout';
import { USER_PERMISSIONS, USER_ROLES } from '@/shared/types/permissions';
import { formatDate } from '@/shared/utils/date';
import { CAlert, CCol, CFormCheck, CFormInput, CFormSelect, CRow } from '@coreui/react';
import React, { useEffect, useState } from 'react';

export const UserForm: React.FC = () => {
  const { entity: user, error, handleSubmit: submitUser } = useUserForm();

  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [rolesError, setRolesError] = useState('');
  useEffect(() => {
    let active = true;
    void rolesApi
      .getOptions()
      .then((options) => {
        if (active) setRoles(options);
      })
      .catch(() => {
        if (active) setRolesError('Failed to load roles.');
      });
    return () => {
      active = false;
    };
  }, []);

  const [formData, setFormData] = useState<CreateUserData>({
    firstName: '',
    lastName: '',
    email: '',
    role: USER_ROLES.CSAgent,
    status: false,
  });
  const [previousUser, setPreviousUser] = useState(user);

  if (user !== previousUser) {
    setPreviousUser(user);
    setFormData(
      user
        ? {
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            role: user.roles?.[0]?.name || USER_ROLES.CSAgent,
            status: user.status || false,
          }
        : {
            firstName: '',
            lastName: '',
            email: '',
            role: USER_ROLES.CSAgent,
            status: false,
          },
    );
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!roles.some((role) => role.name === formData.role)) return;
    void submitUser(formData);
  };

  return (
    <PageLayout>
      <EntityForm
        title={user ? 'Edit User' : 'Create User'}
        isEdit={!!user}
        error={error || rolesError}
        submitButtonDisabled={roles.length === 0}
        listEndpoint={API_ENDPOINTS.USERS.LIST}
        onSubmit={handleSubmit}
        requiredPermission={USER_PERMISSIONS.UsersEdit}
      >
        <CRow>
          <CCol md={6}>
            <div className="mb-3">
              <CFormInput
                type="text"
                name="firstName"
                label="First Name *"
                value={formData.firstName}
                onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
                required
              />
            </div>
          </CCol>
          <CCol md={6}>
            <div className="mb-3">
              <CFormInput
                type="text"
                name="lastName"
                label="Last Name *"
                value={formData.lastName}
                onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
                required
              />
            </div>
          </CCol>
        </CRow>

        <CRow>
          <CCol md={6}>
            <div className="mb-3">
              <CFormInput
                type="email"
                name="email"
                label="Email *"
                value={formData.email}
                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                required
              />
            </div>
          </CCol>
          <CCol md={6}>
            <div className="mb-3">
              <CFormSelect
                name="role"
                label="Role *"
                value={roles.some((role) => role.name === formData.role) ? formData.role : ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, role: e.target.value }))}
                required
              >
                <option value="">Select a role</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.name}>
                    {role.name}
                  </option>
                ))}
              </CFormSelect>
            </div>
          </CCol>
        </CRow>

        <div className="mb-3">
          <CFormCheck
            name="status"
            label="Active"
            checked={formData.status}
            onChange={(e) => setFormData((prev) => ({ ...prev, status: e.target.checked }))}
          />
        </div>

        {user?.lastModifiedBy && (
          <CAlert color="info" className="mt-3">
            <strong>Last Modified:</strong> {user.lastModifiedBy.name} ({user.lastModifiedBy.email})
            {user.lastModifiedBy.timestamp && (
              <div>
                <small>{formatDate(user.lastModifiedBy.timestamp)}</small>
              </div>
            )}
          </CAlert>
        )}
      </EntityForm>
    </PageLayout>
  );
};
